<div class="checkout-confirmation">
    <div class="max-w-[1200px] mx-auto" *ngIf="order$ | async as order">
        <div class="checkout-confirmation__hero">
            <div class="checkout-confirmation__hero-icon">
                <img src="assets/icons/check.svg" width="20" height="20" alt="Check">
            </div>
            <div>
                <h1 class="checkout-confirmation__title">Thank you, {{ order.customer?.firstName }}!</h1>
                <div class="mt-3 space-y-1">
                    <p class="text-[14px] text-gray-700">Your order has been received.</p>
                    <p class="checkout-confirmation__master">
                        Master order (MAIN): <strong>#{{ masterOrderCode(order) }}</strong>
                    </p>
                    <p class="text-[14px] text-gray-700">Will update once Suppliers confirm quantities. Most suppliers confirm &amp; ship products within 3 days.</p>
                </div>
            </div>
        </div>

        <div class="checkout-confirmation__grid">
            <div class="lg:col-span-2">
                <div class="checkout-confirmation__panel overflow-hidden">
                    <div class="p-6 pb-0">
                        <h2 class="text-[20px] font-medium text-black">Order Summary</h2>
                    </div>

                    <div class="p-6 pt-4 space-y-5">
                        <ng-container *ngFor="let group of (groupedLines$ | async)">
                            <div class="checkout-confirmation__supplier">
                                <div class="checkout-confirmation__supplier-head">
                                    <div>
                                        <h3 class="text-[16px] text-black mb-1">
                                            <ng-container *ngIf="group.channelOrderCode; else channelPending">
                                                Supplier order #{{ group.channelOrderCode }}
                                            </ng-container>
                                            <ng-template #channelPending>{{ group.supplierName }}</ng-template>
                                        </h3>
                                        <p class="text-[16px] text-black">{{ group.supplierName }}</p>
                                    </div>
                                    <div class="text-right">
                                        <p class="text-[16px] text-black">{{ group.itemCount }} products</p>
                                        <p class="text-[16px] text-black mt-1">Order Amount: {{ group.totalValue | formatPrice }}</p>
                                    </div>
                                </div>

                                <vsf-order-summary-table [lines]="group.lines"></vsf-order-summary-table>
                            </div>
                        </ng-container>

                        <vsf-order-totals
                            [subtotal]="getOriginalSubtotal(order)"
                            [discountAmount]="getTotalDiscount(order)"
                            [vat]="order.totalWithTax - order.total"
                            [total]="order.totalWithTax"
                            subtotalLabel="Sub Total:"
                            discountLabel="Discount:"
                            [shippingLabel]="shippingCostLabel(order)"
                            [shippingAmount]="shippingCost(order)"
                            vatLabel="VAT:"
                            totalLabel="Total"
                        ></vsf-order-totals>
                    </div>
                </div>
            </div>

            <div class="space-y-6">
                <div class="checkout-confirmation__panel p-6">
                    <div class="flex items-center gap-2 mb-4">
                        <h3 class="text-[18px] font-medium text-black">Addresses</h3>
                    </div>

                    <div class="space-y-6">
                        <div>
                            <h4 class="checkout-confirmation__subsection-title">Shipping address</h4>
                            <div class="text-[14px] text-black leading-relaxed">
                                <p *ngIf="order.shippingAddress?.fullName">{{ order.shippingAddress?.fullName }}</p>
                                <p *ngIf="order.shippingAddress?.streetLine1">{{ order.shippingAddress?.streetLine1 }}</p>
                                <p *ngIf="order.shippingAddress?.city">{{ order.shippingAddress?.city }}, {{ order.shippingAddress?.country }}</p>
                                <p *ngIf="order.shippingAddress?.postalCode">{{ order.shippingAddress?.postalCode }}</p>
                                <p *ngIf="order.shippingAddress?.phoneNumber">{{ order.shippingAddress?.phoneNumber }}</p>
                            </div>
                        </div>

                        <div>
                            <h4 class="checkout-confirmation__subsection-title">Billing address</h4>
                            <ng-container *ngIf="order.billingAddress?.streetLine1; else noBillingAddress">
                                <p
                                    *ngIf="isBillingSameAsShipping(order)"
                                    class="text-[14px] text-black mb-2"
                                >
                                    Same as shipping address
                                </p>
                                <div class="text-[14px] text-black leading-relaxed">
                                    <p *ngIf="order.billingAddress?.fullName">{{ order.billingAddress?.fullName }}</p>
                                    <p *ngIf="order.billingAddress?.company">{{ order.billingAddress?.company }}</p>
                                    <p *ngIf="order.billingAddress?.streetLine1">{{ order.billingAddress?.streetLine1 }}</p>
                                    <p *ngIf="order.billingAddress?.streetLine2">{{ order.billingAddress?.streetLine2 }}</p>
                                    <p *ngIf="order.billingAddress?.city">
                                        {{ order.billingAddress?.city }}<ng-container *ngIf="order.billingAddress?.province">, {{ order.billingAddress?.province }}</ng-container>
                                        <ng-container *ngIf="order.billingAddress?.country"> {{ order.billingAddress?.country }}</ng-container>
                                    </p>
                                    <p *ngIf="order.billingAddress?.postalCode">{{ order.billingAddress?.postalCode }}</p>
                                    <p *ngIf="order.billingAddress?.phoneNumber">{{ order.billingAddress?.phoneNumber }}</p>
                                </div>
                            </ng-container>
                            <ng-template #noBillingAddress>
                                <p class="text-[14px] text-black">No billing address on this order.</p>
                            </ng-template>
                        </div>
                    </div>
                </div>

                <div class="checkout-confirmation__panel p-6">
                    <h3 class="checkout-confirmation__billing-title">Billing info</h3>
                    <div class="space-y-4">
                        <div *ngFor="let payment of order.payments" class="flex items-start gap-3">
                            <div class="checkout-confirmation__payment-dot"></div>
                            <div>
                                <p class="text-[14px] font-medium text-black">{{ payment.method }}</p>
                                <p class="text-[14px] text-black mt-1" *ngIf="payment.metadata?.card_last4">Paid with: **** **** {{ payment.metadata.card_last4 }}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="checkout-confirmation__panel p-6 bg-transparent">
                    <h3 class="checkout-confirmation__help-title">Need Help?</h3>
                    <p class="checkout-confirmation__help-text">Tell us on email what's wrong and we'll reply within 1-2 business days</p>
                    <a [href]="supportMailto" class="checkout-confirmation__help-link">
                        <img src="assets/icons/email.svg" width="20" height="20" alt="Email">
                        Contact via email
                    </a>
                </div>
            </div>
        </div>
    </div>

    <ng-container *ngIf="notFound$ | async">
        <div class="text-center py-20">
            <h2 class="checkout-confirmation__not-found-title">Page not found</h2>
            <p class="text-gray-500 mb-8">We couldn't find the order confirmation details you're looking for.</p>
            <button class="checkout-confirmation__not-found-btn" [routerLink]="['/']" aria-label="Return to home">Return to Home</button>
        </div>
    </ng-container>
</div>
